/**
 * Entry point. The customer pastes one line, and nothing else:
 *
 *   <script src="https://cdn.example/loader.js" data-site-key="..." async></script>
 *
 * That is the whole integration. The API address is compiled into this file, the
 * same way Zendesk, Tawk.to, Crisp and Intercom all do it — a customer has no way
 * of knowing it and no reason to care.
 *
 * This file carries no business logic and no secrets. The site key is public by
 * design — it identifies a tenant, it does not authorise one. What actually gates a
 * request is the Origin check the backend performs, which a copied script cannot
 * forge from a browser.
 */
import { detectLocale } from './locale.js';
import { loadConfig } from './config.js';
import { mount } from './panel.js';
import { assetBase, declareFont, fallbackImages } from './assets.js';
import { currentScript, readSiteKey, readApiBase } from './tag.js';
import { setAuthToken } from './auth.js';

const HOST_TAG = 'chatbot-widget-host';

// Last-resort copy for an unauthorized site_key — used only if Drupal's own
// configured text (chatbot_core.settings:invalid_site_key_messages) didn't
// come back either, e.g. the RAG couldn't reach Drupal for it. See
// config.js's loadConfig() and ADR-0041.
const UNAUTHORIZED_MESSAGE = {
  es: '[chatbot] El site_key no es válido, no está autorizado para este dominio, o el chatbot está suspendido.',
  en: '[chatbot] The site_key is invalid, not authorized for this domain, or the chatbot is suspended.',
};

// Captured while the module body still runs: document.currentScript is null once
// execution reaches an async callback.
const SCRIPT = currentScript();

async function boot() {
  const script = SCRIPT;
  const siteKey = readSiteKey(script);

  if (!siteKey) {
    // A missing key is a customer integration mistake, not a runtime failure.
    // Say so once in the console and stop; never render a broken widget.
    console.warn('[chatbot] missing data-site-key on the script tag');
    return;
  }

  // Refuse to mount twice if the tag ends up on the page more than once, which
  // happens easily with tag managers and partial page reloads.
  if (document.querySelector(HOST_TAG)) return;

  const apiBase = readApiBase(script);
  setAuthToken(script);
  const locale = detectLocale(script);
  const config = await loadConfig(apiBase, siteKey, locale);

  if (config.unauthorized) {
    // Same treatment as a missing key: a wrong/unauthorized site_key found
    // at the very first load is an integration mistake, not a transient
    // failure — warn once and never mount. Distinct from a *send* failing
    // after the widget already rendered, which shows a visitor-facing
    // message instead (panel.js, ADR-0041) — there's no visitor-facing UI
    // to show anything in here at all.
    console.warn(config.consoleMessage || UNAUTHORIZED_MESSAGE[locale] || UNAUTHORIZED_MESSAGE.es);
    return;
  }

  const base = assetBase(script);
  declareFont(base);

  const host = document.createElement(HOST_TAG);
  document.body.appendChild(host);

  // Shadow DOM rather than an iframe. The reconnection token for live chat lives
  // in localStorage, and inside an iframe on our own domain that storage would be
  // third-party — exactly what Safari and Firefox restrict. In the shadow tree the
  // widget shares the customer page's first-party storage.
  mount(host.attachShadow({ mode: 'open' }), { config, siteKey, apiBase, locale, images: fallbackImages(base) });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
